/**
 * ProductSearch.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { queryMatchesSearchText, productRowSearchHaystack } from '../utils/siteSearch';
import './ProductSearch.css';

const ProductSearch = ({ selectedProductIds = [], onSelectionChange, excludeProductId = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const searchRef = useRef(null);

  const fetchAllProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/products', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out the current product being edited
        const filtered = data.filter(p => p.id !== excludeProductId);
        setAllProducts(filtered);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [excludeProductId]);

  // Fetch all products on mount
  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setShowDropdown(true);

    if (!term.trim()) {
      setSearchResults(allProducts.slice(0, 10));
      return;
    }

    const results = allProducts.filter(product =>
      queryMatchesSearchText(productRowSearchHaystack(product), term, { title: product.name }),
    ).slice(0, 10);

    setSearchResults(results);
  };

  const handleSelectProduct = (product) => {
    if (!selectedProductIds.includes(product.id)) {
      const newSelection = [...selectedProductIds, product.id];
      onSelectionChange(newSelection);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveProduct = (productId) => {
    const newSelection = selectedProductIds.filter(id => id !== productId);
    onSelectionChange(newSelection);
  };

  const getSelectedProducts = () => {
    return allProducts.filter(p => selectedProductIds.includes(p.id));
  };

  return (
    <div className="product-search-component" ref={searchRef}>
      <div className="product-search-input-wrapper">
        <input
          type="text"
          className="product-search-input"
          placeholder="Search products by name, author, artist, or SKU..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
        />
        <span className="search-icon">🔍</span>
      </div>

      {showDropdown && searchResults.length > 0 && (
        <div className="product-search-dropdown">
          {searchResults.map(product => (
            <div
              key={product.id}
              className={`product-search-item ${selectedProductIds.includes(product.id) ? 'selected' : ''}`}
              onClick={() => handleSelectProduct(product)}
            >
              <div className="product-search-item-info">
                <div className="product-search-item-name">{product.name}</div>
                <div className="product-search-item-meta">
                  {product.author || product.artist} • {product.category} • ${(product.price_cents / 100).toFixed(2)}
                </div>
              </div>
              {selectedProductIds.includes(product.id) && (
                <span className="product-search-check">✓</span>
              )}
            </div>
          ))}
        </div>
      )}

      {getSelectedProducts().length > 0 && (
        <div className="product-search-selected">
          <div className="selected-label">Selected Related Products ({getSelectedProducts().length}):</div>
          <div className="selected-products-list">
            {getSelectedProducts().map(product => (
              <div key={product.id} className="selected-product-tag">
                <span className="selected-product-name">{product.name}</span>
                <button
                  type="button"
                  className="remove-product-btn"
                  onClick={() => handleRemoveProduct(product.id)}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSearch;

